import { moment } from "../date";
import type { AttendanceList, AttendanceListItem, AutomaticBreakRestore } from "../types";

/**
 * How a check-in takes the day's automatic breaks back, so the day is judged as a whole again at
 * its next check-out — an automatic break decided at a check-out that turned out not to be the
 * day's last would otherwise stay, and the real break taken after it would come on top.
 *
 * The rows say how each break was carved, because `getAutomaticBreak` carves at an entry's end or
 * start only where no other work entry touches it: work on both sides of the break is a split and
 * folds back into one entry, work only before it was shortened at its end, work only after it was
 * shortened at its start. A break with no work beside it at all has had its day edited around it
 * since, and is left where it is.
 *
 * @param dayAttendances - Every entry of the person's day.
 * @returns One restore per automatic break that can be taken back.
 */
export function getAutomaticBreakRestores(dayAttendances: AttendanceList): AutomaticBreakRestore[] {
	const restores: AutomaticBreakRestore[] = [];
	for (const attendance of dayAttendances) {
		if (attendance.type !== "break" || !attendance.isAutomatic || !attendance.endTime) continue;
		const restore = getRestore(
			dayAttendances,
			attendance.id,
			attendance.startTime,
			attendance.endTime,
		);
		if (restore) restores.push(restore);
	}
	return restores;
}

function getRestore(
	dayAttendances: AttendanceList,
	breakId: string,
	startTime: string,
	endTime: string,
): AutomaticBreakRestore | null {
	const before = getWorkEntryTouching(dayAttendances, "endTime", startTime);
	const after = getWorkEntryTouching(dayAttendances, "startTime", endTime);

	if (before && after?.endTime) {
		return {
			breakId,
			attendanceId: before.id,
			startTime: before.startTime,
			endTime: after.endTime,
			mergedAttendanceId: after.id,
		};
	}
	if (before) {
		return {
			breakId,
			attendanceId: before.id,
			startTime: before.startTime,
			endTime,
			mergedAttendanceId: null,
		};
	}
	if (after?.endTime) {
		return {
			breakId,
			attendanceId: after.id,
			startTime,
			endTime: after.endTime,
			mergedAttendanceId: null,
		};
	}
	return null;
}

/** The work entry whose start or end lies exactly at a moment, if one does. */
function getWorkEntryTouching(
	dayAttendances: AttendanceList,
	side: "startTime" | "endTime",
	time: string,
): AttendanceListItem | null {
	for (const attendance of dayAttendances) {
		if (attendance.type !== "work") continue;
		const edge = attendance[side];
		if (edge && moment(edge).isSame(time)) return attendance;
	}
	return null;
}
